import { useSidebar } from "@elit-hair/elit-ui";
import { LayoutGroup, motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ChatHeader } from "@/components/ai-chat/chat-header";
import { ChatInput } from "@/components/ai-chat/chat-input";
import { ChatMessages } from "@/components/ai-chat/chat-messages";
import { DarwinAvatar } from "@/components/ai-chat/darwin-avatar";
import { ErrorReportModal } from "@/components/ai-chat/error-report-modal";
import { DarwinEmptyStateProps } from "@/components/ai-chat/types/ai-chat-panel.types";
import { useChatStream } from "@/components/ai-chat/use-chat-stream";
import {
    DARWIN_AVATAR_LAYOUT_ID,
    DARWIN_AVATAR_LAYOUT_TRANSITION,
    DARWIN_AVATAR_SIZE,
    DARWIN_EMPTY_STATE_AVATAR_PADDING,
} from "@/constants/ai-chat";
import { cn } from "@/lib/utils";
import { ChatMessage } from "@/types/chatbot.types";
import { DarwinAvatarState } from "@/types/darwin-avatar.types";

function getAvatarState(isBusy: boolean, hasInput: boolean): DarwinAvatarState {
    if (isBusy) {
        return "waiting";
    }

    if (hasInput) {
        return "input";
    }

    return "idle";
}

function DarwinEmptyState({ avatarState, showAvatar }: DarwinEmptyStateProps) {
    const { t } = useTranslation();
    const avatarFrameSize = DARWIN_AVATAR_SIZE.EMPTY_STATE + DARWIN_EMPTY_STATE_AVATAR_PADDING * 2;

    return (
        <div className="flex h-full min-h-0 flex-col items-center justify-center gap-5 overflow-visible px-6 py-8 text-center">
            <div
                className="relative flex items-center justify-center overflow-visible"
                style={{ width: avatarFrameSize, height: avatarFrameSize }}
            >
                <motion.div
                    aria-hidden
                    className="absolute size-48 rounded-full bg-violet-500/25 blur-3xl dark:bg-fuchsia-400/20"
                    initial={false}
                    animate={{ opacity: showAvatar ? 1 : 0 }}
                    transition={{ duration: 0.2 }}
                />
                {showAvatar ? (
                    <motion.div
                        layoutId={DARWIN_AVATAR_LAYOUT_ID}
                        transition={DARWIN_AVATAR_LAYOUT_TRANSITION}
                        className="relative flex items-center justify-center overflow-visible"
                    >
                        <DarwinAvatar size={DARWIN_AVATAR_SIZE.EMPTY_STATE} state={avatarState} />
                    </motion.div>
                ) : null}
            </div>
            <motion.div
                className="space-y-1"
                initial={false}
                animate={{ opacity: showAvatar ? 1 : 0, y: showAvatar ? 0 : 8 }}
                transition={{ duration: 0.2 }}
            >
                <p className="text-base font-semibold text-foreground">{t("darwin")}</p>
                <p className="text-sm text-muted-foreground">{t("how_can_i_help_you")}</p>
            </motion.div>
        </div>
    );
}

export function AiChatPanel() {
    const { t } = useTranslation();
    const { toggleSidebar } = useSidebar();
    const [isErrorReportModalOpen, setIsErrorReportModalOpen] = useState(false);
    const [reportedMessage, setReportedMessage] = useState<ChatMessage | null>(null);

    const { messages, input, handleInputChange, handleSubmit, clearHistory, isLoading, isStreaming, stopStreaming } =
        useChatStream();

    const hasMessages = messages.length > 0;
    const isBusy = isLoading || isStreaming;
    const avatarState = getAvatarState(isBusy, Boolean(input.trim()));

    const handleReportError = (message: ChatMessage) => {
        setReportedMessage(message);
        setIsErrorReportModalOpen(true);
    };

    const handleCloseErrorModal = () => {
        setIsErrorReportModalOpen(false);
        setReportedMessage(null);
    };

    return (
        <LayoutGroup id="darwin-chat-panel">
            <div className="darwin-chat-panel flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border/80 bg-background/40 shadow-inner dark:border-white/15">
                <div className="relative z-10 flex h-full min-h-0 flex-col">
                    <ChatHeader
                        avatarState={avatarState}
                        showAvatar={hasMessages}
                        onClose={toggleSidebar}
                        onClearHistory={clearHistory}
                    />

                    <div className="relative min-h-0 flex-1 overflow-hidden">
                        <div
                            className={cn(
                                "absolute inset-0 overflow-visible transition-opacity duration-200",
                                hasMessages ? "pointer-events-none opacity-0" : "opacity-100"
                            )}
                            aria-hidden={hasMessages}
                        >
                            <DarwinEmptyState avatarState={avatarState} showAvatar={!hasMessages} />
                        </div>

                        {hasMessages ? (
                            <div className="absolute inset-0 overflow-hidden">
                                <ChatMessages
                                    messages={messages}
                                    isLoading={isBusy}
                                    onReportError={handleReportError}
                                />
                            </div>
                        ) : null}
                    </div>

                    <div className="shrink-0 border-t border-border/80 bg-background/50 backdrop-blur-md dark:border-white/15">
                        <ChatInput
                            value={input}
                            onChange={handleInputChange}
                            onSubmit={handleSubmit}
                            isLoading={isLoading}
                            isStreaming={isStreaming}
                            onStop={stopStreaming}
                            placeholder={hasMessages ? t("type_your_message") : t("ask_me_anything")}
                            useTypewriterPlaceholder={!hasMessages}
                            className="chatbot-input"
                        />
                    </div>
                </div>

                <ErrorReportModal
                    isOpen={isErrorReportModalOpen}
                    onClose={handleCloseErrorModal}
                    conversation={messages.map((msg) => ({
                        role: msg.role,
                        content: msg.content,
                    }))}
                    reportedMessage={
                        reportedMessage
                            ? {
                                  role: reportedMessage.role,
                                  content: reportedMessage.content,
                              }
                            : undefined
                    }
                />
            </div>
        </LayoutGroup>
    );
}
